import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, filter, map, of, shareReplay, switchMap, take, timer } from 'rxjs';

import { AttendanceService } from '../../services/attendance.service';
import { AttendanceLocationService } from '../../services/attendance-location.service';
import { CompaniesService } from '../../../services/companies.service';
import {
  AttendanceCheckOutDialogComponent,
  AttendanceCheckOutDialogData,
} from '../attendance-check-out-dialog/attendance-check-out-dialog.component';

interface AttendanceVM {
  checkedIn: boolean;
  /** Live "HH:MM:SS" since check-in. */
  elapsedLabel: string;
  /** Check-in time, e.g. "09:15". */
  sinceLabel: string;
}

/**
 * The badge reader, in the header: Check In when you arrive, Check Out when you leave.
 * Visible on every page, and — because the session is persisted — it still shows you as
 * checked in after a reload, in a second tab, or the next morning.
 */
@Component({
    selector: 'app-attendance-check-in',
    imports: [CommonModule, MatIconModule, MatTooltipModule],
    templateUrl: './attendance-check-in.component.html',
    styleUrls: ['./attendance-check-in.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttendanceCheckInComponent implements OnInit {
  private readonly attendance = inject(AttendanceService);
  private readonly location = inject(AttendanceLocationService);
  private readonly companiesService = inject(CompaniesService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  saving = false;

  // Attendance is not finished end to end yet, so the whole widget is switched off: no chip is
  // rendered and no stale session is prompted for. Flip to true to bring it back. Any session
  // left open in storage is preserved, and will be offered for check-out when it is.
  readonly attendanceEnabled = false;

  // Ticks every second so the elapsed label counts up — only while checked in: each tick is an
  // app-wide change detection pass, and this chip is on every page.
  readonly vm$ = this.attendance.session$.pipe(
    switchMap(session => session ? timer(0, 1000).pipe(map(() => session)) : of(session)),
    map((session): AttendanceVM => {
      if (!session) {
        return { checkedIn: false, elapsedLabel: '', sinceLabel: '' };
      }
      const start = new Date(session.startTimeIso);
      return {
        checkedIn: true,
        elapsedLabel: this.formatElapsed(this.attendance.elapsedSeconds()),
        sinceLabel: `${this.pad(start.getHours())}:${this.pad(start.getMinutes())}`,
      };
    }),
  );

  ngOnInit(): void {
    if (!this.attendanceEnabled) return;

    // A check-out writes a timesheet entry, which is company-scoped — so wait until the
    // active company is known before offering to close a session left open overnight.
    this.companiesService.getManagedCompanyIdObsValue().pipe(
      filter(id => !!id),
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.closeStaleSession());
  }

  checkIn(): void {
    if (!this.attendanceEnabled) return;

    // Stamp the time first: the working day starts when they press the button, not whenever
    // the device finishes locating itself (or the user gets round to answering the prompt).
    this.attendance.checkIn();
    this.snackBar.open('Checked in. Have a good day.', 'Close', { duration: 3000 });

    this.location.capture().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(verdict => this.attendance.setCheckInPlace(this.location.label(verdict)));
  }

  checkOut(): void {
    const session = this.attendance.session;
    if (!session || this.saving) return;

    // Locate in parallel with the dialog rather than before it, so a slow fix never leaves the
    // user staring at an unresponsive button. shareReplay keeps the single result for both the
    // dialog's display and the save.
    const place$ = this.location.capture().pipe(
      map(v => this.location.label(v)),
      shareReplay(1),
    );
    place$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe();

    // A session that has crossed midnight cannot end "now": the entry is recorded against the
    // day it began, so an end time of 01:00 would land before the 09:00 check-in. Offer the
    // capped end instead, the same as any other session left open too long.
    const stale = this.attendance.isStale();
    this.openCheckOutDialog({
      startTimeIso: session.startTimeIso,
      suggestedEnd: stale ? this.attendance.suggestedEndFor(session) : new Date(),
      stale,
      checkInPlace: session.checkInPlace,
      checkOutPlace$: place$,
    });
  }

  /**
   * On load, a session that ran past midnight or past the maximum session length means the
   * user left without checking out. Rather than silently booking the whole elapsed span — a
   * weekend would become 60 hours — ask when they actually left, pre-filled with a capped time.
   */
  private closeStaleSession(): void {
    const session = this.attendance.session;
    if (!session || !this.attendance.isStale()) return;
    this.openCheckOutDialog({
      startTimeIso: session.startTimeIso,
      suggestedEnd: this.attendance.suggestedEndFor(session),
      stale: true,
      checkInPlace: session.checkInPlace,
      // No check-out location: they left hours ago, so wherever the device is now is not where
      // they were when they left. Recording today's position as yesterday's exit would be false.
      checkOutPlace$: of(undefined),
    });
  }

  private openCheckOutDialog(data: AttendanceCheckOutDialogData): void {
    this.dialog.open(AttendanceCheckOutDialogComponent, {
      width: 'min(420px, calc(100vw - 32px))',
      panelClass: 'bc-sheet',
      autoFocus: false,
      // A stale session must be resolved — dismissing it would just re-prompt on the next load.
      disableClose: data.stale,
      data,
    }).afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((end: Date | null) => {
      if (end) this.save(end, data.checkOutPlace$);
    });
  }

  private save(end: Date, place$: Observable<string | undefined>): void {
    this.saving = true;
    place$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(place => this.persist(end, place));
  }

  private persist(end: Date, place?: string): void {
    // Success can come back with an empty body, so branch on next/error rather than on the
    // response value — mistaking a successful save for a failure would leave the user checked
    // in after their day was already recorded.
    this.attendance.checkOut(end, place).subscribe({
      next: () => {
        this.saving = false;
        this.snackBar.open('Checked out. Your attendance has been recorded.', 'Close', { duration: 4000 });
      },
      error: err => {
        this.saving = false;
        const msg = err?.error?.message
          || 'Could not record your check-out. You are still checked in — please try again.';
        this.snackBar.open(msg, 'Close', { duration: 6000 });
      },
    });
  }

  private formatElapsed(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }
}
